import { create } from 'zustand';
import type { Seller, Product, ChatMessage, ChatSession } from '../types';
import { api } from '../services/api';

interface AppStore {
  seller: Seller | null;
  products: Product[];
  isOnline: boolean;
  isLoading: boolean;
  isInitialLoad: boolean;
  hasMore: boolean;
  page: number;
  userLocation: { lat: number; lng: number } | null;
  
  // Auth State
  isAuthModalOpen: boolean;

  // Chat State
  chatSessions: ChatSession[];
  activeChatId: string | null;
  activeChatMessages: ChatMessage[];
  activeProduct: Product | null;
  isChatOpen: boolean;
  totalUnreadCount: number;

  // Actions
  setSeller: (seller: Seller | null) => void;
  setOnlineStatus: (status: boolean) => void;
  setLoading: (loading: boolean) => void;
  setUserLocation: (loc: { lat: number; lng: number } | null) => void;
  setAuthModalOpen: (open: boolean) => void;
  
  fetchProducts: (page?: number, isInitial?: boolean) => Promise<void>;
  resetProducts: () => void;
  addProduct: (product: Omit<Product, 'id'>, imageBlob?: Blob) => Promise<void>;
  updateProduct: (id: string, updates: Partial<Omit<Product, 'id'>>, imageBlob?: Blob) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  toggleProductStatus: (id: string) => Promise<void>;
  checkSession: () => Promise<void>;

  // Chat Actions
  setChatOpen: (open: boolean) => void;
  openChat: (chatId: string, product?: Product) => Promise<void>;
  fetchChatSessions: () => Promise<void>;
  fetchMessages: (chatId: string) => Promise<void>;
  sendChatMessage: (chatId: string, recipient: string, text: string, productId: string, productName: string) => Promise<void>;
  markAsRead: (chatId: string) => Promise<void>;
  closeChat: () => void;
}

// Initial count from localStorage for SWR pattern
const savedUnreadCount = parseInt(localStorage.getItem('agro_unread_count') || '0', 10);

export const useAppStore = create<AppStore>((set, get) => ({
  seller: null,
  products: [],
  isOnline: navigator.onLine,
  isLoading: false,
  isInitialLoad: true,
  hasMore: true,
  page: 1,
  userLocation: null,
  
  isAuthModalOpen: false,

  chatSessions: [],
  activeChatId: null,
  activeChatMessages: [],
  activeProduct: null,
  isChatOpen: false,
  totalUnreadCount: savedUnreadCount,

  setSeller: (seller) => {
    set({ seller, chatSessions: [], activeChatMessages: [], activeChatId: null, isChatOpen: false, totalUnreadCount: 0 });
    if (seller) {
      get().fetchChatSessions();
    }
  },
  setOnlineStatus: (isOnline) => set({ isOnline }),
  setLoading: (isLoading) => set({ isLoading }),
  setUserLocation: (userLocation) => set({ userLocation }),
  setAuthModalOpen: (isAuthModalOpen) => set({ isAuthModalOpen }),

  fetchProducts: async (pageNumber = 1, isInitial = false) => {
    if (isInitial) set({ isInitialLoad: true });
    else set({ isLoading: true });

    try {
      const { userLocation } = get();
      const response = await api.getProducts(pageNumber, 8, userLocation || undefined);
      
      set(state => ({
        products: pageNumber === 1 ? response.data : [...state.products, ...response.data],
        page: pageNumber,
        hasMore: response.hasMore,
        isInitialLoad: false,
        isLoading: false
      }));
    } catch (error) {
      console.error('Fetch products failed:', error);
      set({ isInitialLoad: false, isLoading: false });
    }
  },

  resetProducts: () => {
    set({ products: [], page: 1, hasMore: true, isInitialLoad: true });
    get().fetchProducts(1, true);
  },

  addProduct: async (productData, imageBlob) => {
    const newProduct = await api.createProduct(productData as any, imageBlob);
    set(state => ({ products: [newProduct, ...state.products] }));
  },

  updateProduct: async (id, updates, imageBlob) => {
    const updated = await api.updateProduct(id, updates, imageBlob);
    set(state => ({
      products: state.products.map(p => p.id === id ? updated : p)
    }));
  },

  deleteProduct: async (id) => {
    await api.deleteProduct(id);
    set(state => ({
      products: state.products.filter(p => p.id !== id)
    }));
  },

  toggleProductStatus: async (id) => {
    const updated = await api.toggleProductStatus(id);
    set(state => ({
      products: state.products.map(p => p.id === id ? updated : p)
    }));
  },

  checkSession: async () => {
    try {
      const user = await api.auth.getSession();
      set({ seller: user });
      if (user) {
        await get().fetchChatSessions();
      }
    } catch (e: any) {
      if (e.message === 'TokenExpiredError') {
        try {
          const refreshed = await api.auth.refresh();
          set({ seller: refreshed });
          if (refreshed) await get().fetchChatSessions();
        } catch {
          set({ seller: null });
        }
      } else {
        set({ seller: null });
      }
    }
  },

  setChatOpen: (isChatOpen) => {
    set({ isChatOpen });
    if (!isChatOpen) {
      const { activeChatId } = get();
      if (activeChatId) get().markAsRead(activeChatId);
    }
  },
  
  fetchChatSessions: async () => {
    try {
      const sessions = await api.getChatSessions();
      const totalUnreadCount = sessions.reduce((acc, s) => acc + s.unreadCount, 0);
      
      // Update cache
      localStorage.setItem('agro_unread_count', totalUnreadCount.toString());
      
      set({ chatSessions: sessions, totalUnreadCount });
    } catch (error: any) {
      if (error.message === 'Unauthorized') {
        set({ chatSessions: [], totalUnreadCount: 0 });
        localStorage.setItem('agro_unread_count', '0');
      } else {
        console.error('Fetch chat sessions failed:', error);
      }
    }
  },

  openChat: async (chatId, product) => {
    set({ activeChatId: chatId, activeProduct: product || null, isChatOpen: true });
    await get().fetchMessages(chatId);
    await get().markAsRead(chatId);
  },

  fetchMessages: async (chatId) => {
    const messages = await api.getMessages(chatId);
    set({ activeChatMessages: messages });
  },

  sendChatMessage: async (chatId, recipient, text, productId, productName) => {
    const newMsg = await api.sendMessage(chatId, recipient, text, productId, productName);
    set(state => ({ 
      activeChatMessages: [...state.activeChatMessages, newMsg],
      chatSessions: state.chatSessions.map(s => s.id === chatId ? { ...s, lastMessage: text, lastTimestamp: Date.now() } : s)
    }));
  },

  markAsRead: async (chatId) => {
    await api.markChatAsRead(chatId);
    const session = get().chatSessions.find(s => s.id === chatId);
    if (session && session.unreadCount > 0) {
      // In a real app, call API to mark as read
      // api.markChatAsRead(chatId); 
      set(state => {
        const newSessions = state.chatSessions.map(s => s.id === chatId ? { ...s, unreadCount: 0 } : s);
        const totalUnreadCount = newSessions.reduce((acc, s) => acc + s.unreadCount, 0);
        localStorage.setItem('agro_unread_count', totalUnreadCount.toString());
        return { chatSessions: newSessions, totalUnreadCount };
      });
    }
  },

  closeChat: () => {
    const { activeChatId } = get();
    if (activeChatId) get().markAsRead(activeChatId);
    set({ activeChatId: null, isChatOpen: false, activeChatMessages: [], activeProduct: null });
  }
}));